import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import type {
  RuntimeCronJobRecordDto,
} from "@/lib/api/generated/control-api.schemas";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/shared/status-badge";
import { formatTimestamp, toastMutationError } from "@/lib/utils/formatting";
import {
  getGetApiRuntimeCronJobsQueryKey,
  useDeleteApiRuntimeCronJobsCronJobId,
  useGetApiRuntimeCronJobs,
  usePostApiRuntimeCronJobs,
} from "@/lib/api/generated/runtime-cron-jobs/runtime-cron-jobs";
import { RuntimeCronJobEditorDialog } from "./runtime-cron-job-editor-dialog";
import { RuntimeCronJobRunsDialog } from "./runtime-cron-job-runs-dialog";
import {
  createRuntimeCronDraft,
  describeSchedule,
  getCronOwnerUserId,
  getCronScope,
  mapRunStatus,
  requireCronJobId,
  type RuntimeCronJobRecord,
  type RuntimeCronScope,
} from "./runtime-cron-utils";

export function RuntimeCronJobsCard({ tenantId }: { tenantId: string }) {
  const [editingJob, setEditingJob] = useState<RuntimeCronJobRecordDto | null>(null);
  const [runsJob, setRunsJob] = useState<RuntimeCronJobRecordDto | null>(null);
  const [activeScope, setActiveScope] = useState<RuntimeCronScope>("user");
  const queryClient = useQueryClient();
  const userJobsQueryParams = { tenantId, includeDisabled: true, scope: "user" as const };
  const tenantJobsQueryParams = { tenantId, includeDisabled: true, scope: "tenant" as const };
  const userJobsQuery = useGetApiRuntimeCronJobs(userJobsQueryParams, {
    query: {
      enabled: tenantId.trim().length > 0,
    },
  });
  const tenantJobsQuery = useGetApiRuntimeCronJobs(tenantJobsQueryParams, {
    query: {
      enabled: tenantId.trim().length > 0,
    },
  });

  const saveMutation = usePostApiRuntimeCronJobs({
    mutation: {
      onSuccess: () => {
        invalidateCronJobQueries();
      },
    },
  });

  const deleteMutation = useDeleteApiRuntimeCronJobsCronJobId({
    mutation: {
      onSuccess: () => {
        invalidateCronJobQueries();
      },
    },
  });

  function invalidateCronJobQueries() {
    void queryClient.invalidateQueries({
      queryKey: getGetApiRuntimeCronJobsQueryKey(userJobsQueryParams),
    });
    void queryClient.invalidateQueries({
      queryKey: getGetApiRuntimeCronJobsQueryKey(tenantJobsQueryParams),
    });
  }

  const isUserScope = activeScope === "user";
  const jobsQuery = isUserScope ? userJobsQuery : tenantJobsQuery;
  const jobs = jobsQuery.data ?? [];

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div className="space-y-1">
            <CardTitle>Runtime Cron Jobs</CardTitle>
            <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
              Cron schedules are API-backed and scope-aware. Personal schedules stay user-owned;
              tenant schedules are operator-managed.
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={() => setEditingJob(createRuntimeCronDraft(tenantId, activeScope))}
          >
            <Plus className="mr-1 h-4 w-4" />
            New Schedule
          </Button>
        </CardHeader>
        <CardContent>
          <Tabs value={activeScope} onValueChange={(value) => setActiveScope(value as RuntimeCronScope)}>
            <TabsList>
              <TabsTrigger value="user">My Schedules</TabsTrigger>
              <TabsTrigger value="tenant">Tenant Schedules</TabsTrigger>
            </TabsList>
            <TabsContent value={activeScope} className="mt-3">
              {jobsQuery.isLoading ? (
                <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
                  Loading runtime cron jobs…
                </p>
              ) : jobs.length === 0 ? (
                <div
                  className="rounded-[var(--radius-sm)] border px-3 py-4 text-sm"
                  style={{ borderColor: "var(--border)", color: "var(--muted-foreground)" }}
                >
                  {isUserScope
                    ? "No personal runtime cron jobs yet."
                    : "No tenant runtime cron jobs yet."}
                </div>
              ) : (
                <div className="space-y-3">
                  {jobs.map((job) => {
                    const cronJobId = requireCronJobId(job);
                    const scope = getCronScope(job);
                    return (
                <div
                  key={cronJobId}
                  className="rounded-[var(--radius-sm)] border p-3"
                  style={{ borderColor: "var(--border)" }}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
                          {job.name}
                        </span>
                        <StatusBadge status={job.enabled ? "active" : "paused"} />
                        {job.lastRunStatus ? (
                          <StatusBadge status={mapRunStatus(job.lastRunStatus)} />
                        ) : null}
                      </div>
                      {job.description ? (
                        <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                          {job.description}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setRunsJob(job)}
                      >
                        Runs
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingJob(job)}
                      >
                        Edit
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={saveMutation.isPending || deleteMutation.isPending}
                        onClick={async () => {
                          try {
                            const record: RuntimeCronJobRecord = {
                              ...job,
                              cronJobId,
                              tenantId,
                              name: job.name ?? "",
                              enabled: !job.enabled,
                              createdAt: job.createdAt ?? new Date().toISOString(),
                              updatedAt: new Date().toISOString(),
                              schedule: job.schedule ?? { kind: "cron", expr: "0 9 * * *", tz: "UTC" },
                              payload: job.payload ?? { kind: "agentTurn", message: "" },
                              sessionTarget: job.sessionTarget ?? "isolated",
                              wakeMode: job.wakeMode ?? "now",
                              scopeKind: scope,
                              ownerUserId: getCronOwnerUserId(job),
                            };
                            await saveMutation.mutateAsync({
                              data: {
                                record,
                              },
                            });
                            toast.success(job.enabled ? "Runtime cron job disabled" : "Runtime cron job enabled");
                          } catch (error) {
                            toast.error(toastMutationError(error, "Failed to update runtime cron job"));
                          }
                        }}
                      >
                        {job.enabled ? "Disable" : "Enable"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        disabled={saveMutation.isPending || deleteMutation.isPending}
                        onClick={async () => {
                          const confirmed = window.confirm(
                            `Delete runtime cron job "${job.name}"? This cannot be undone.`,
                          );
                          if (!confirmed) {
                            return;
                          }
                          try {
                            const params = {
                              tenantId,
                              scope,
                            };
                            await deleteMutation.mutateAsync({
                              cronJobId,
                              params,
                            });
                            toast.success("Runtime cron job deleted");
                          } catch (error) {
                            toast.error(toastMutationError(error, "Failed to delete runtime cron job"));
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-3 text-xs md:grid-cols-2 xl:grid-cols-4">
                    <DisplayField label="Cron Job ID" value={cronJobId} mono />
                    <DisplayField label="Agent" value={job.agentId ?? "Default agent"} mono />
                    <DisplayField label="Scope" value={scope} />
                    <DisplayField label="Owner" value={getCronOwnerUserId(job) ?? "—"} mono />
                    <DisplayField label="Schedule" value={describeSchedule(job)} />
                    <DisplayField label="Session Target" value={job.sessionTarget ?? "isolated"} mono />
                    <DisplayField label="Wake Mode" value={job.wakeMode ?? "now"} mono />
                    <DisplayField label="Next Run" value={formatTimestamp(job.nextRunAt ?? undefined)} />
                    <DisplayField label="Last Run" value={formatTimestamp(job.lastRunAt ?? undefined)} />
                    <DisplayField label="Source" value={job.sourceChannel ?? "runtime"} mono />
                  </div>
                  {job.lastError ? (
                    <div
                      className="mt-3 rounded-[var(--radius-sm)] border px-3 py-2 text-xs"
                      style={{ borderColor: "var(--danger-border)", background: "var(--danger-soft)" }}
                    >
                      {job.lastError}
                    </div>
                  ) : null}
                </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      <RuntimeCronJobEditorDialog
        job={editingJob}
        open={Boolean(editingJob)}
        pending={saveMutation.isPending}
        onOpenChange={(open) => {
          if (!open) {
            setEditingJob(null);
          }
        }}
        onSave={async (record: RuntimeCronJobRecord) => {
          await saveMutation.mutateAsync({ data: { record } });
          setEditingJob(null);
        }}
      />
      <RuntimeCronJobRunsDialog
        tenantId={tenantId}
        scope={activeScope}
        job={runsJob}
        open={Boolean(runsJob)}
        onOpenChange={(open) => {
          if (!open) {
            setRunsJob(null);
          }
        }}
      />
    </>
  );
}

function DisplayField({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="space-y-1">
      <div style={{ color: "var(--muted-foreground)" }}>{label}</div>
      <div
        className={mono ? "font-mono text-[11px]" : undefined}
        style={{ color: "var(--text-strong)" }}
      >
        {value}
      </div>
    </div>
  );
}
